import type { IProjectRepository } from "@/application/ports/repositories";
import type { Project, ProjectListParams, CreateProjectResponse } from "@/domain";
import type { CreateProjectRequest } from "@/domain/project";
import type { DocumentDownloadResponse } from "@/domain/documentBlocks";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";

export class ProjectRepository implements IProjectRepository {
  async createProject(
    data: CreateProjectRequest & { beneficiary_uuid: string },
  ): Promise<CreateProjectResponse> {
    // Empty strings from the form would fail backend validation — send undefined instead
    const clean = (value: string | null | undefined) => (value === "" ? undefined : value);

    return unwrap(
      sdk.createProjectForBeneficiary({
        path: { beneficiary_uuid: data.beneficiary_uuid },
        body: {
          title: data.title,
          tax_incentive_type: data.tax_incentive_type,
          subtype: data.subtype,
          fiscal_quota_uuid: data.fiscal_quota_uuid,
          description: clean(data.description),
          fundraising_date: clean(data.fundraising_date),
          production_start_date: clean(data.production_start_date),
          production_end_date: clean(data.production_end_date),
          fiscal_year: data.fiscal_year ?? undefined,
          city: clean(data.city),
        },
      }),
    );
  }

  async getProjects(params?: ProjectListParams): Promise<{
    projects: Project[];
    total: number;
    total_unfiltered_count: number;
    page: number;
    total_pages: number;
  }> {
    const response = await unwrap(sdk.getProjects({ query: params }));

    const limit = params?.limit || 100;
    const offset = params?.offset || 0;
    const currentPage = Math.floor(offset / limit) + 1;
    const totalPages = Math.ceil((response.total_count || 0) / limit);

    return {
      projects: response.items,
      total: response.total_count || response.items.length,
      total_unfiltered_count:
        response.total_unfiltered_count ?? (response.total_count || response.items.length),
      page: currentPage,
      total_pages: totalPages,
    };
  }

  async getProject(id: string): Promise<Project> {
    return unwrap(sdk.getProject({ path: { project_uuid: id } }));
  }

  async getBeneficiaryProjects(beneficiaryUuid: string): Promise<Project[]> {
    const response = await unwrap(
      sdk.getBeneficiaryProjects({ path: { beneficiary_uuid: beneficiaryUuid } }),
    );
    return response.items;
  }

  async updateProject(
    id: string,
    data: Partial<CreateProjectRequest>,
  ): Promise<CreateProjectResponse> {
    return unwrap(sdk.updateProject({ path: { project_uuid: id }, body: data }));
  }

  async deleteProject(id: string): Promise<void> {
    await unwrap(sdk.deleteProject({ path: { project_uuid: id } }));
  }

  async archiveProject(id: string): Promise<CreateProjectResponse> {
    return unwrap(sdk.updateProject({ path: { project_uuid: id }, body: { status: "inactive" } }));
  }

  async unarchiveProject(id: string): Promise<CreateProjectResponse> {
    return unwrap(sdk.updateProject({ path: { project_uuid: id }, body: { status: "active" } }));
  }

  async getPosterInfo(projectUuid: string): Promise<DocumentDownloadResponse> {
    return unwrap(sdk.getProjectPoster({ path: { project_uuid: projectUuid } }));
  }

  async uploadPoster(projectUuid: string, file: File): Promise<void> {
    await unwrap(
      sdk.createProjectPoster({
        path: { project_uuid: projectUuid },
        body: { poster_file: file },
      }),
    );
  }

  async deletePoster(projectUuid: string): Promise<void> {
    await unwrap(sdk.deleteProjectPoster({ path: { project_uuid: projectUuid } }));
  }
}
